"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuContent, NavigationMenuTrigger, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="1919" className="flex gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-20 bg-muted/20 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-sm font-medium tracking-tight text-foreground/70 hover:text-secondary hover:underline decoration-secondary/50 underline-offset-8 transition-all duration-300 font-sans">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 p-8 items-center justify-center min-h-screen">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-10 w-48 bg-muted/20 animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col items-center justify-center min-h-screen gap-8 px-6 w-full">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-3xl font-sans font-bold tracking-tighter text-foreground hover:text-primary transition-colors block w-full text-center py-4 px-6" onClick={onClose}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobileMenuOpen]);
  return <>
      <header id="cabecera-principal" className={cn("dark sticky top-0 z-50 w-full transition-all duration-500 ease-in-out border-b border-transparent", isScrolled ? "bg-background/80 backdrop-blur-md border-primary/10 shadow-xl" : "bg-background")}>
        <div className="container mx-auto px-4 h-16 md:h-20 flex items-center justify-between">
          
          <Link to="/" className="z-[60] relative flex items-center gap-4 flex-shrink-0">
            <img src="https://recla360.com/wp-content/uploads/2026/05/Screenshot_20260506_113201_X.jpg" alt="Recla360 Logo" className="!w-[230px] !h-[66px]" loading="lazy" />
          </Link>

          <div className="hidden md:block">
            <MenuProvider menu_id="16" className="w-full">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          <Button variant="ghost" size="icon" className="md:hidden z-[60] relative hover:bg-primary/10 border border-transparent hover:border-primary/20" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label={isMobileMenuOpen ? "Cerrar menú" : "Abrir menú"}>
            {isMobileMenuOpen ? <X className="h-6 w-6 text-primary" /> : <Menu className="h-6 w-6 text-foreground" />}
          </Button>

          <div className={cn("fixed top-0 left-0 w-screen min-h-screen bg-[#0b1020] z-[999999] overflow-y-auto transition-all duration-500 ease-in-out md:hidden", isMobileMenuOpen ? "block opacity-100 visible translate-y-0 pointer-events-auto" : "hidden opacity-0 invisible -translate-y-full pointer-events-none")}>
            <Button variant="ghost" size="icon" className="fixed top-6 right-6 z-[1000000] w-12 h-12 text-foreground hover:bg-primary/10" onClick={() => setIsMobileMenuOpen(false)}>
              <X className="h-8 w-8" />
            </Button>
            <MenuProvider menu_id="16" className="w-full flex justify-center">
              <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
          </div>
        </div>
      </header>

      {!isMobileMenuOpen && <Link to="https://wa.me/34602505574?text=Hola,%20quiero%20información%20sobre%20cómo%20conseguir%20más%20clientes%20para%20mi%20negocio" newTab className="fixed bottom-4 right-4 z-[60]">
          <Button size="icon" className="h-14 w-14 rounded-lg bg-[#25D366] hover:bg-[#22c35e] text-white shadow-xl transition-all duration-300 hover:scale-110 active:scale-95 border-none cursor-pointer">
            <svg viewBox="0 0 24 24" fill="currentColor" className="size-8" aria-hidden="true">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.72.937 3.659 1.432 5.631 1.433h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
          </Button>
        </Link>}
    </>;
}